import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authStorage } from '../services/authStorage';
import { authApi, ApiError } from '../services/authApi';
import '../styles/CertificationStatusTable.css';
// @ts-ignore - jsPDF types
import jsPDF from 'jspdf';
// @ts-ignore - jspdf-autotable types
import 'jspdf-autotable';

interface TestProgress {
    totalTests: number;
    approvedTests: number;
    ignoredTests: number;
    pendingTests: number;
}

export interface CertificationStatus {
    id: number;
    companyName: string;
    lastUpdatedDate: string | null;
    lastUpdatedBy: {
        id: number;
        username: string;
    } | null;
    certificationDate: string | null;
    certificationStatus: 'CERTIFIED' | 'IN_PROGRESS' | 'NOT_STARTED';
    testProgress: TestProgress;
}

interface CertificationStatusResponse {
    companies: CertificationStatus[];
}

interface CertificationStatusTableProps {
    onStatusClick?: (company: CertificationStatus) => void;
    onCompanyClick?: (company: CertificationStatus) => void;
}

type SortField = 'companyName' | 'lastUpdatedDate' | 'certificationDate' | 'certificationStatus';
type SortDirection = 'asc' | 'desc';

const CertificationStatusTable: React.FC<CertificationStatusTableProps> = ({ onStatusClick, onCompanyClick }) => {
    const { t } = useTranslation();
    const [certificationData, setCertificationData] = useState<CertificationStatus[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [sortField, setSortField] = useState<SortField>('companyName');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [hideTestCompanies, setHideTestCompanies] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [openDropdown, setOpenDropdown] = useState<number | null>(null);
    const pageSize = 10;

    useEffect(() => {
        loadCertificationStatus();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.actions-dropdown')) {
                setOpenDropdown(null);
            }
        };

        if (openDropdown !== null) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [openDropdown]);

    const loadCertificationStatus = async () => {
        try {
            setLoading(true);
            setError('');
            const token = authStorage.getAccessToken();

            if (!token) {
                setError('Authentication token not found. Please log in again.');
                return;
            }

            const response: CertificationStatusResponse = await authApi.getCertificationStatus(token);
            setCertificationData(response.companies);
        } catch (error) {
            console.error('Failed to load certification status:', error);
            if (error instanceof ApiError) {
                setError(error.message);
            } else {
                setError('Failed to load certification status. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return '↕';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    const sortedData = [...certificationData].sort((a, b) => {
        let aValue: any = a[sortField];
        let bValue: any = b[sortField];

        // Handle null values
        if (aValue === null) return 1;
        if (bValue === null) return -1;

        // Convert to comparable values
        if (sortField === 'lastUpdatedDate' || sortField === 'certificationDate') {
            aValue = aValue ? new Date(aValue).getTime() : 0;
            bValue = bValue ? new Date(bValue).getTime() : 0;
        } else if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const filteredData = sortedData.filter(company => {
        const matchesSearch = company.companyName.toLowerCase().includes(searchTerm.toLowerCase());
        const isTestCompany = /test|testing/i.test(company.companyName);

        if (hideTestCompanies && isTestCompany) {
            return false;
        }

        return matchesSearch;
    });

    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, hideTestCompanies, sortField, sortDirection]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const formatDate = (dateString: string | null): string => {
        if (!dateString) return '-';
        try {
            // Handle format: "2025-12-04 12:34" or ISO format
            const date = new Date(dateString);
            return date.toISOString().replace('T', ' ').substring(0, 16);
        } catch {
            return dateString;
        }
    };

    const exportToCSV = () => {
        // Define CSV headers
        const headers = ['Company Name', 'Date Last Updated', 'Date Certified', 'Status', 'Tests Approved', 'Total Tests', 'Ignored Tests'];

        // Format date for CSV (shorter format to avoid Excel ########)
        const formatDateForCSV = (dateString: string | null): string => {
            if (!dateString) return '-';
            try {
                const date = new Date(dateString);
                return date.toISOString().split('T')[0]; // YYYY-MM-DD format only
            } catch {
                return dateString;
            }
        };

        // Sort by company name alphabetically before export
        const sortedData = [...filteredData].sort((a, b) => {
            return a.companyName.localeCompare(b.companyName, 'sv', { sensitivity: 'base' });
        });

        // Map data to CSV rows
        const rows = sortedData.map(company => {
            let status = '';
            let testsApproved = company.testProgress.approvedTests.toString();
            let totalTests = company.testProgress.totalTests.toString();
            let ignoredTests = company.testProgress.ignoredTests.toString();

            if (company.certificationStatus === 'CERTIFIED') {
                status = 'CERTIFIED';
            } else if (company.certificationStatus === 'IN_PROGRESS') {
                status = 'IN_PROGRESS';
            } else if (company.certificationStatus === 'NOT_STARTED') {
                status = 'NOT_STARTED';
            }

            return [
                company.companyName,
                formatDateForCSV(company.lastUpdatedDate),
                formatDateForCSV(company.certificationDate),
                status,
                testsApproved,
                totalTests,
                ignoredTests
            ];
        });

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => {
                // Escape cells that contain commas or quotes
                if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
                    return `"${cell.replace(/"/g, '""')}"`;
                }
                return cell;
            }).join(','))
        ].join('\n');

        // Add UTF-8 BOM for proper Swedish character encoding
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `certification_status_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadPDF = () => {
        try {
            // Sort by company name alphabetically before generating PDF
            const sortedData = [...filteredData].sort((a, b) => {
                return a.companyName.localeCompare(b.companyName, 'sv', { sensitivity: 'base' });
            });

            const generatedAt = new Date().toLocaleString();
            const dateStamp = new Date().toISOString().split('T')[0];

            // Create new PDF document
            const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation

            // Add title
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('Certification Status Report', 14, 20);

            // Add generation date
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100);
            doc.text(`Generated: ${generatedAt}`, 14, 28);

            // Format date for PDF
            const formatDateForPDF = (dateString: string | null): string => {
                if (!dateString) return '-';
                try {
                    const date = new Date(dateString);
                    return date.toISOString().split('T')[0]; // YYYY-MM-DD
                } catch {
                    return dateString;
                }
            };

            // Prepare table data
            const tableData = sortedData.map(company => {
                let status = '';
                if (company.certificationStatus === 'CERTIFIED') {
                    status = 'CERTIFIED';
                } else if (company.certificationStatus === 'IN_PROGRESS') {
                    status = 'IN_PROGRESS';
                } else if (company.certificationStatus === 'NOT_STARTED') {
                    status = 'NOT_STARTED';
                }

                return [
                    company.companyName,
                    formatDateForPDF(company.lastUpdatedDate),
                    formatDateForPDF(company.certificationDate),
                    status,
                    company.testProgress.approvedTests.toString(),
                    company.testProgress.totalTests.toString(),
                    company.testProgress.ignoredTests.toString()
                ];
            });

            // Add table using autoTable
            (doc as any).autoTable({
                startY: 35,
                head: [['Company Name', 'Date Last Updated', 'Date Certified', 'Status', 'Tests Approved', 'Total Tests', 'Ignored Tests']],
                body: tableData,
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                },
                headStyles: {
                    fillColor: [245, 245, 245],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250],
                },
                margin: { top: 35 },
            });

            // Save the PDF with clean filename
            doc.save(`Certification_Status_Report_${dateStamp}.pdf`);

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        }
    };

    const renderStatus = (company: CertificationStatus) => {
        let content: React.ReactNode;

        if (company.certificationStatus === 'CERTIFIED') {
            content = (
                <span className="status-badge certified">
                    CERTIFIED
                </span>
            );
        } else if (company.certificationStatus === 'IN_PROGRESS') {
            const { totalTests, approvedTests, ignoredTests } = company.testProgress;
            content = (
                <div className="status-progress-badge">
                    <span className="status-progress-main">
                        {approvedTests} out of {totalTests} test approved
                    </span>
                    {ignoredTests > 0 && (
                        <span className="status-progress-sub">
                            {ignoredTests} ignored
                        </span>
                    )}
                </div>
            );
        } else if (company.certificationStatus === 'NOT_STARTED') {
            const { totalTests, approvedTests } = company.testProgress;
            if (totalTests === 0) {
                content = <span className="status-text">-</span>;
            } else {
                content = (
                    <div className="status-progress-badge">
                        <span className="status-progress-main">
                            {approvedTests} out of {totalTests} test approved
                        </span>
                    </div>
                );
            }
        } else {
            content = <span className="status-text">-</span>;
        }

        if (onStatusClick) {
            return (
                <button
                    type="button"
                    className="status-click-target"
                    onClick={() => onStatusClick(company)}
                >
                    {content}
                </button>
            );
        }

        return content;
    };

    if (loading) {
        return (
            <div className="certification-status-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading certification status...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="certification-status-container">
                <div className="error-state">
                    <p className="error-message">{error}</p>
                    <button onClick={loadCertificationStatus} className="retry-button">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="certification-status-container">
            <div className="certification-management-header">
                <h1>Certification Status</h1>
            </div>

            <div className="controls-row">
                <div className="controls-left">
                    <div className="search-section">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search companies by name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                    </div>
                    <div className="filter-section">
                        <label className="filter-checkbox">
                            <input
                                type="checkbox"
                                checked={hideTestCompanies}
                                onChange={(e) => setHideTestCompanies(e.target.checked)}
                            />
                            <span>Hide test/testing companies</span>
                        </label>
                    </div>
                </div>
                <div className="controls-right">
                    <button onClick={downloadPDF} className="print-report-button">
                        📄 Download PDF
                    </button>
                    <button onClick={exportToCSV} className="export-csv-button">
                        📊 Export CSV
                    </button>
                </div>
            </div>

            <div className="results-summary">
                <p>Total companies: {filteredData.length}</p>
            </div>

            <div className="certification-table-container">
                <table className="certification-table">
                    <thead>
                        <tr>
                            <th className="sortable" onClick={() => handleSort('companyName')}>
                                Name {getSortIcon('companyName')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('lastUpdatedDate')}>
                                Date Last Updated {getSortIcon('lastUpdatedDate')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('certificationDate')}>
                                Date Certified {getSortIcon('certificationDate')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('certificationStatus')}>
                                Status {getSortIcon('certificationStatus')}
                            </th>
                            <th className="actions-column">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length > 0 ? (
                            paginatedData.map((company) => (
                                <tr key={company.id} className="table-row">
                                    <td className="company-name-cell">
                                        <button
                                            type="button"
                                            className="company-name-button"
                                            onClick={() => onCompanyClick?.(company)}
                                        >
                                            <div className="company-name-wrapper">
                                                <img
                                                    src={`https://api.dicebear.com/7.x/shapes/svg?seed=${encodeURIComponent(company.companyName)}`}
                                                    alt={company.companyName}
                                                    className="company-avatar"
                                                />
                                                <span className="company-name">{company.companyName}</span>
                                            </div>
                                        </button>
                                    </td>
                                    <td className="date-cell">
                                        {formatDate(company.lastUpdatedDate)}
                                    </td>
                                    <td className="date-cell">
                                        {formatDate(company.certificationDate)}
                                    </td>
                                    <td className="status-cell">
                                        {renderStatus(company)}
                                    </td>
                                    <td className="actions-cell">
                                        <div className="actions-dropdown">
                                            <button
                                                type="button"
                                                className="actions-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenDropdown(openDropdown === company.id ? null : company.id);
                                                }}
                                                title="Actions"
                                            >
                                                ⋮
                                            </button>
                                            {openDropdown === company.id && (
                                                <div className="dropdown-menu">
                                                    {company.certificationStatus === 'CERTIFIED' && (
                                                        <button
                                                            type="button"
                                                            className="dropdown-item"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenDropdown(null);
                                                                onStatusClick?.(company);
                                                            }}
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style={{ marginRight: '8px' }}>
                                                                <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm-.5 2.5A.5.5 0 0 1 5 6h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zM5 8a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1H5z" />
                                                                <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z" />
                                                            </svg>
                                                            Go to Certificate
                                                        </button>
                                                    )}
                                                    {(company.certificationStatus === 'IN_PROGRESS' || company.certificationStatus === 'NOT_STARTED') && (
                                                        <button
                                                            type="button"
                                                            className="dropdown-item"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenDropdown(null);
                                                                onStatusClick?.(company);
                                                            }}
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style={{ marginRight: '8px' }}>
                                                                <path d="M9.5 0a.5.5 0 0 1 .5.5.5.5 0 0 0 .5.5.5.5 0 0 1 .5.5V2a.5.5 0 0 1-.5.5h-5A.5.5 0 0 1 5 2v-.5a.5.5 0 0 1 .5-.5.5.5 0 0 0 .5-.5.5.5 0 0 1 .5-.5h3z" />
                                                                <path d="M3 2.5a.5.5 0 0 1 .5-.5H4a.5.5 0 0 0 0-1h-.5A1.5 1.5 0 0 0 2 2.5v12A1.5 1.5 0 0 0 3.5 16h9a1.5 1.5 0 0 0 1.5-1.5v-12A1.5 1.5 0 0 0 12.5 1H12a.5.5 0 0 0 0 1h.5a.5.5 0 0 1 .5.5v12a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-12z" />
                                                                <path d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
                                                            </svg>
                                                            Go to Test Suite
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="dropdown-item"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenDropdown(null);
                                                            onCompanyClick?.(company);
                                                        }}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style={{ marginRight: '8px' }}>
                                                            <path d="M2.5 14V1.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5V14h10V1.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5V14h.5a.5.5 0 0 1 0 1H2a.5.5 0 0 1 0-1h.5z" />
                                                            <path d="M4.5 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H5a.5.5 0 0 1-.5-.5v-1zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H8a.5.5 0 0 1-.5-.5v-1zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm-6 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H5a.5.5 0 0 1-.5-.5v-1zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H8a.5.5 0 0 1-.5-.5v-1zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm-6 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H5a.5.5 0 0 1-.5-.5v-1zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H8a.5.5 0 0 1-.5-.5v-1z" />
                                                        </svg>
                                                        Go to Company Page
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="no-data">
                                    {searchTerm ? 'No companies found matching your search.' : 'No companies available.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {filteredData.length > 0 && totalPages > 1 && (
                <div className="pagination">
                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                    >
                        «
                    </button>
                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                    >
                        ‹
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                            key={page}
                            className={`pagination-button ${currentPage === page ? 'active' : ''}`}
                            onClick={() => setCurrentPage(page)}
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                    >
                        ›
                    </button>
                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        »
                    </button>
                </div>
            )}
        </div>
    );
};

export default CertificationStatusTable;
